// src/features/playlists/usePlaylistMedia.ts

import { useVideos } from '@/features/media/useVideos';
import { useStreams } from '@/features/streams/useStreams';
import type { MediaType } from '@/lib/types/Media';
import type { PlaylistMediaView } from '@/lib/types/Playlist';

export interface UsePlaylistMediaResult {
  /** Media views matching the playlist's mediaType source (videos library or live streams). */
  items: PlaylistMediaView[];
  isLoading: boolean;
}

/**
 * Source the media a playlist's items can reference, by the playlist's mediaType:
 * 'video' playlists draw from the videos library, 'video_stream' playlists from
 * live streams. Only the matching query runs — the other stays disabled so a
 * stream playlist never pulls a (potentially 30k+ item) videos payload.
 * Legacy mediaTypes v3 has no source for (audio etc.) resolve to an empty list.
 */
export function usePlaylistMedia(mediaType: MediaType): UsePlaylistMediaResult {
  const isStream = mediaType === 'video_stream';
  // refetchOnMount: false — playlist views tolerate cached data, so revisits
  // within gcTime cost zero network. Freshness still self-heals via query
  // invalidation on media mutations, window-focus refetch, and Videos/Streams
  // page visits (which refetch on their own policies into the same cache).
  const { videos, isLoading: videosLoading } = useVideos({ enabled: !isStream, refetchOnMount: false });
  const { streams, isLoading: streamsLoading } = useStreams({ enabled: isStream, refetchOnMount: false });

  return {
    items: isStream ? streams : videos,
    isLoading: isStream ? streamsLoading : videosLoading,
  };
}
